import { useEffect, useMemo, useRef, useState } from 'react';
import AssistantComposer from '@/ui/components/AiAssistant/AssistantComposer';
import { useHistory } from 'react-router-dom';
import { useAssistantContext } from 'components/AiAssistant';
import { Button } from '@/ui/design-system/components/ui/button';
import { SidebarTrigger } from '@/ui/design-system/components/ui/sidebar';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbList,
    BreadcrumbPage,
    Skeleton,
    SpateAiIcon,
    Text,
} from '@/ui/design-system/components';
import {
    useDeleteReport,
    useDuplicateReport,
    useReports,
} from '@/utils/hooks/reports';
import useReportBuildStore from '@/state/stores/reportBuildStore';
import { buildReportPrompt } from './reportBuild';
import ReportListItem from './ReportListItem';

// Most-recently-updated first. The backend list endpoint already sorts this
// way, but the react-query cache drifts out of order: optimistic create and
// duplicate append the new row to the end, and the list never refetches on
// focus — so a just-built report would otherwise sit at the bottom. Sorting at
// render keeps the order correct regardless of how rows entered the cache.
// Parse to timestamps rather than comparing ISO strings: the server emits
// offset-form (`+00:00`) while optimistic rows use `Z`, which don't sort
// lexicographically against each other.
const updatedTime = (report) =>
    new Date(report.updated ?? report.created ?? 0).getTime() || 0;

function ReportList() {
    const { data: reports = [], isLoading } = useReports();
    const duplicateReport = useDuplicateReport();
    const deleteReport = useDeleteReport();

    const sortedReports = useMemo(
        () => [...reports].sort((a, b) => updatedTime(b) - updatedTime(a)),
        [reports],
    );

    // Clone server-side into a fresh row (new id, version 1, " (copy)" title) and
    // stay on the list — the hook's optimistic insert shows the copy immediately.
    const handleDuplicate = (report) => duplicateReport.mutate(report.id);

    const handleDelete = (id) => deleteReport.mutate(id);

    if (isLoading) {
        return (
            <div className="flex flex-col gap-2.5">
                {[0, 1, 2].map((i) => (
                    <Skeleton key={i} className="h-[88px] rounded-xl" />
                ))}
            </div>
        );
    }

    if (reports.length === 0) {
        return (
            <Text color="muted" size="sm">
                No reports yet — describe one above to get started.
            </Text>
        );
    }

    return (
        <div className="flex flex-col gap-2.5">
            {sortedReports.map((report) => (
                <ReportListItem
                    key={report.id}
                    report={report}
                    onDuplicate={handleDuplicate}
                    onDelete={handleDelete}
                />
            ))}
        </div>
    );
}

function Reports() {
    const [input, setInput] = useState('');
    const textareaRef = useRef(null);
    const history = useHistory();
    const { togglePanel, openPanel, sendMessage } = useAssistantContext();
    const startBuild = useReportBuildStore((s) => s.startBuild);

    // Focus the prompt on entry so the user can start typing immediately —
    // building a report is this page's primary action (unlike the panel
    // composer, where the input is secondary to the conversation).
    useEffect(() => {
        textareaRef.current?.focus();
    }, [textareaRef]);

    // A report build is an ordinary assistant chat: it runs in the panel (in a
    // fresh session, saved to history like any other) and the model calls
    // create_report when the research is done, which navigates to the report.
    // The store's prompt only paints the /reports/new placeholder meanwhile.
    // Page context is skipped — "/reports" adds nothing to the research.
    const handleSubmit = (e) => {
        e?.preventDefault();
        const prompt = input.trim();
        if (!prompt) return;
        startBuild(prompt);
        setInput('');
        sendMessage(buildReportPrompt(prompt), {
            newChat: true,
            skipPageContext: true,
            source: 'reports',
        });
        openPanel();
        history.push('/reports/new');
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="flex min-w-0 items-center gap-3">
                    <SidebarTrigger className="md:hidden" />
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem>
                                <BreadcrumbPage>Reports</BreadcrumbPage>
                            </BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>
                </div>
                <Button
                    variant="assistant"
                    leftIcon={<SpateAiIcon />}
                    onClick={togglePanel}
                    className="shrink-0"
                >
                    Spate AI
                </Button>
            </div>
            <div className="mx-auto flex w-full max-w-[796px] flex-col gap-14 px-4 pb-16 pt-6">
                <div className="flex flex-col gap-9">
                    <div className="flex flex-col gap-2">
                        <Text
                            as="h1"
                            size="2xl"
                            className="font-semibold normal-case leading-9"
                        >
                            Build your next report
                        </Text>
                        <Text size="sm" color="muted">
                            Describe what you want to find out. Spate AI gathers
                            data from across the dashboard and turns it into a
                            report that&apos;s ready to export to slides and
                            refreshed automatically.
                        </Text>
                    </div>
                    <AssistantComposer
                        value={input}
                        onValueChange={setInput}
                        onSubmit={handleSubmit}
                        onKeyDown={handleKeyDown}
                        isLoading={false}
                        textareaRef={textareaRef}
                        // The server rejects user messages over 5000 chars
                        // (MAX_USER_MESSAGE_LENGTH); leave room for the
                        // "Create a report: " prefix the submit adds.
                        maxLength={4980}
                        placeholder="Compare the top skincare brands in the US and South Korea."
                        disclosure="label"
                        showFreePreview={false}
                        showModelSelector={false}
                        className="p-0"
                        // White card so it stands out on the gray page (the
                        // home-page composer sits in a white card, where the
                        // default gray surface reads fine); plus a purple focus
                        // ring for a pop of color when the user starts typing.
                        surfaceClassName="bg-background shadow-sm transition-[border-color,box-shadow] duration-150 focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring"
                    />
                </div>
                <section className="flex flex-col gap-4">
                    <Text size="base" weight="medium">
                        My Reports
                    </Text>
                    <ReportList />
                </section>
            </div>
        </div>
    );
}

export default Reports;
